import React, { useMemo } from "react";
import { Spinner, Stack } from "react-bootstrap";

import { GetBacklogAgingResponse } from "src/services/Firebase/data";
import { colors } from "src/styles";
import styled from "styled-components";

interface WidgetProps {
  caption: string;
  preloadedData?: GetBacklogAgingResponse;
}

const BacklogAging = ({ caption, preloadedData }: WidgetProps) => {
  const data = preloadedData;

  const buckets = data?.widgetsData?.backlogAging ?? [];
  const total = useMemo(() => buckets.reduce((acc, b) => acc + b.exceededCount, 0), [buckets]);

  return (
    <WidgetContainer>
      <Stack gap={3}>
        <CardTitle>{caption}</CardTitle>

        {data ? (
          <>
            <BucketList>
              {data.widgetsData?.backlogAging.map((bucket) => {
                const pct = total > 0 ? Math.round((bucket.exceededCount / total) * 100) : 0;
                return (
                  <BucketRow key={bucket.priorityId}>
                    <BucketHeader>
                      <BucketLabel>{bucket.priorityName}</BucketLabel>
                      <BucketCount>
                        {bucket.exceededCount} <BucketPct>{pct}%</BucketPct>
                      </BucketCount>
                    </BucketHeader>
                    <BarTrack>
                      <BarFill $color={bucket.priorityColor} $pct={pct} />
                    </BarTrack>
                  </BucketRow>
                );
              })}
            </BucketList>
            <TotalRow>
              Total exceeded SLA: <strong>{total} WOs</strong>
            </TotalRow>
          </>
        ) : (
          <Spinner animation="border" />
        )}
      </Stack>
    </WidgetContainer>
  );
};

export default BacklogAging;

const WidgetContainer = styled.div`
  display: flex;
  min-width: 0;
  background: #ffffff;
  border: 0.5px solid rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  padding: 16px;
`;

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${colors.grey.dark};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const BucketList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 11px;
  flex: 1;
`;

const BucketRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BucketHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const BucketLabel = styled.span`
  font-size: 12px;
  color: ${colors.grey.dark};
`;

const BucketCount = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.grey.dark3};
`;

const BucketPct = styled.span`
  font-size: 10px;
  font-weight: 400;
  color: ${colors.grey.light10};
`;

const BarTrack = styled.div`
  height: 8px;
  background: ${colors.grey.light3};
  border-radius: 5px;
  overflow: hidden;
`;

const BarFill = styled.div<{ $color: string; $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: ${({ $color }) => $color};
  border-radius: 5px;
  transition: width 0.7s ease;
`;

const TotalRow = styled.div`
  border-top: 0.5px solid ${colors.grey.light11};
  padding-top: 8px;
  font-size: 11px;
  color: ${colors.grey.dark};
`;
